/**
 * Tenant attachment sections (REQ-AGT-003 enmienda 14-Oct-2026).
 *
 * Own knowledge/tools attachments on (also) `use_only` agents. Only the
 * tenant's own entries are ever listed here — the publisher base is never
 * read nor shown. Attach/detach go through the tenant-overlay endpoints.
 */

import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button, Card, Spinner } from '@/components/ui';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Database, Trash2, Wrench, Plus } from 'lucide-react';
import { useI18n } from '@/i18n';
import { useCollections } from '@/hooks/use-knowledge';
import { useMCPServers } from '@/features/mcp/hooks/useMCPServers';
import { getErrorMessage } from '@/lib/api-client';
import {
  useAttachTenantKnowledge,
  useDetachTenantKnowledge,
  useAttachTenantTools,
  useDetachTenantTools,
  useTenantKnowledge,
  useTenantTools,
} from '@/features/agents/hooks/useTenantAttachments';

// =============================================================================
// Own knowledge
// =============================================================================

export function TenantKnowledgeSection({ agentId }: { agentId: string }) {
  const { t } = useI18n();
  const overlay = useTenantKnowledge(agentId);
  const attach = useAttachTenantKnowledge(agentId);
  const detach = useDetachTenantKnowledge(agentId);
  const { data: collectionsPage } = useCollections({ page_size: 100 });
  const allCollections = collectionsPage?.data ?? [];
  const [picking, setPicking] = useState(false);

  const attached = useMemo(
    () => overlay.data?.collections ?? [],
    [overlay.data],
  );
  const attachedNames = useMemo(
    () => new Set(attached.map((c) => c.name)),
    [attached],
  );
  const candidates = useMemo(
    () => (allCollections ?? []).filter((c) => !attachedNames.has(c.name)),
    [allCollections, attachedNames],
  );

  async function handleAttach(collectionId: string) {
    try {
      await attach.mutateAsync({ collection_ids: [collectionId] });
      setPicking(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function handleDetach(name: string) {
    try {
      await detach.mutateAsync(name);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <Card className="p-6 mt-4">
      <div className="flex items-center gap-2 mb-1">
        <Database className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">
          {t('admin.agents.editor.useOnly.ownKnowledgeTitle') ||
            'Your knowledge'}
        </h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {t('admin.agents.editor.useOnly.ownKnowledgeDescription') ||
          'Connect your own collections. Only what you attach here is listed — the publisher content stays hidden.'}
      </p>

      {overlay.isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : attached.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-2">
          {t('admin.agents.editor.useOnly.ownKnowledgeEmpty') ||
            'No own collections attached yet.'}
        </p>
      ) : (
        <div className="space-y-1 mb-3">
          {attached.map((c) => (
            <div
              key={c.name}
              className="flex items-center gap-2 text-sm border-b last:border-0 py-1.5"
            >
              <span className="flex-1 truncate font-medium">{c.name}</span>
              <span className="text-xs text-muted-foreground">
                {t('admin.agents.editor.useOnly.priority') || 'priority'}{' '}
                {c.priority}
              </span>
              <button
                type="button"
                aria-label={`Detach ${c.name}`}
                onClick={() => handleDetach(c.name)}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!picking ? (
        <Button
          size="sm"
          variant="outline"
          type="button"
          onClick={() => setPicking(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('admin.agents.editor.useOnly.attachCollection') ||
            'Attach collection'}
        </Button>
      ) : (
        <div className="border rounded-md p-3 bg-muted/30 max-h-64 overflow-auto">
          {candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2 text-center">
              {t('admin.agents.editor.useOnly.noCollectionsLeft') ||
                'No more collections to attach.'}
            </p>
          ) : (
            <div className="space-y-1">
              {candidates.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleAttach(c.id)}
                  disabled={attach.isPending}
                  className="w-full text-left flex items-center gap-2 px-2 py-2 rounded hover:bg-accent"
                >
                  <span className="flex-1 truncate text-sm font-medium">
                    {c.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {c.document_count ?? 0}{' '}
                    {t('admin.agents.editor.useOnly.documents') || 'docs'}
                  </span>
                </button>
              ))}
            </div>
          )}
          <div className="flex justify-end mt-2">
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={() => setPicking(false)}
            >
              {t('common.cancel') || 'Cancel'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// =============================================================================
// Own tools
// =============================================================================

export function TenantToolsSection({ agentId }: { agentId: string }) {
  const { t } = useI18n();
  const overlay = useTenantTools(agentId);
  const attach = useAttachTenantTools(agentId);
  const detach = useDetachTenantTools(agentId);
  const [picking, setPicking] = useState(false);
  const [opsDraft, setOpsDraft] = useState('');

  const attached = useMemo(() => overlay.data?.servers ?? [], [overlay.data]);

  async function handleAttach(server: string) {
    try {
      const operations = opsDraft
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      await attach.mutateAsync({
        servers: [{ server, operations: operations.length ? operations : ['*'] }],
      });
      setPicking(false);
      setOpsDraft('');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function handleDetach(server: string) {
    try {
      await detach.mutateAsync(server);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <Card className="p-6 mt-4">
      <div className="flex items-center gap-2 mb-1">
        <Wrench className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">
          {t('admin.agents.editor.useOnly.ownToolsTitle') || 'Your tools'}
        </h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {t('admin.agents.editor.useOnly.ownToolsDescription') ||
          'Connect your own MCP servers. Only what you attach here is listed — the publisher tools stay hidden.'}
      </p>

      {overlay.isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : attached.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-2">
          {t('admin.agents.editor.useOnly.ownToolsEmpty') ||
            'No own tools attached yet.'}
        </p>
      ) : (
        <div className="space-y-1 mb-3">
          {attached.map((s) => (
            <div
              key={s.server}
              className="flex items-center gap-2 text-sm border-b last:border-0 py-1.5"
            >
              <span className="flex-1 truncate font-medium">{s.server}</span>
              <span className="text-xs text-muted-foreground truncate max-w-[40%]">
                {(s.operations ?? []).join(', ')}
              </span>
              <button
                type="button"
                aria-label={`Detach ${s.server}`}
                onClick={() => handleDetach(s.server)}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!picking ? (
        <Button
          size="sm"
          variant="outline"
          type="button"
          onClick={() => setPicking(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('admin.agents.editor.useOnly.attachServer') || 'Attach MCP server'}
        </Button>
      ) : (
        <OwnServerPicker
          exclude={attached.map((s) => s.server)}
          opsDraft={opsDraft}
          onOpsDraft={setOpsDraft}
          onPick={handleAttach}
          onClose={() => {
            setPicking(false);
            setOpsDraft('');
          }}
          pending={attach.isPending}
        />
      )}
    </Card>
  );
}

function OwnServerPicker({
  exclude,
  opsDraft,
  onOpsDraft,
  onPick,
  onClose,
  pending,
}: {
  exclude: string[];
  opsDraft: string;
  onOpsDraft: (v: string) => void;
  onPick: (server: string) => void;
  onClose: () => void;
  pending: boolean;
}) {
  const { t } = useI18n();
  const { data: serversPage, isLoading } = useMCPServers({
    pageSize: 100,
    status: 'active',
  });
  const servers = (serversPage?.data ?? []).filter(
    (s) => !exclude.includes(s.id) && !exclude.includes(s.server_type),
  );
  return (
    <div className="border rounded-md p-3 bg-muted/30">
      <div className="space-y-1 mb-2">
        <Label>
          {t('admin.agents.editor.useOnly.operationsLabel') ||
            'Operations (comma-separated, * for all)'}
        </Label>
        <Input
          value={opsDraft}
          onChange={(e) => onOpsDraft(e.target.value)}
          placeholder="*"
        />
      </div>
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner size="sm" />
        </div>
      ) : servers.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2 text-center">
          {t('admin.agents.editor.useOnly.noServersLeft') ||
            'No more MCP servers to attach.'}
        </p>
      ) : (
        <div className="space-y-1 max-h-64 overflow-auto">
          {servers.map((s) => (
            <button
              key={s.id}
              type="button"
              disabled={pending}
              onClick={() => onPick(s.id)}
              className="w-full text-left flex items-center gap-2 px-2 py-2 rounded hover:bg-accent"
            >
              <span className="flex-1 truncate text-sm font-medium">
                {s.name ?? s.id}
              </span>
              <span className="text-xs text-muted-foreground">
                {s.server_type}
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="flex justify-end mt-2">
        <Button size="sm" variant="ghost" type="button" onClick={onClose}>
          {t('common.cancel') || 'Cancel'}
        </Button>
      </div>
    </div>
  );
}
